/**
 * Tela /casos/:id/laudo — edição da narrativa por contestada + export PDF/DOCX (E9.F2.T10/T11). // ux-neutrality:allow doc-comment
 *
 * E9.F2 muda a tela para suportar K contestadas (caso com K análises // ux-neutrality:allow doc-comment
 * `completed`): tabs `a)` / `b)` / `c)` por contestada com badge de // ux-neutrality:allow doc-comment
 * status (rascunho / em edição / aprovada). Cada tab carrega sua // ux-neutrality:allow doc-comment
 * narrative independente; o editor opera sobre a tab ativa. Os botões // ux-neutrality:allow doc-comment
 * "Baixar PDF" / "Baixar DOCX" só habilitam quando TODAS as narratives // ux-neutrality:allow doc-comment
 * existem e estão aprovadas — o backend valida o mesmo invariante // ux-neutrality:allow doc-comment
 * (422 NARRATIVE_NOT_READY / NARRATIVE_NOT_APPROVED). // ux-neutrality:allow doc-comment
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  getLaudoDocxBlob,
  getLaudoPdfBlob,
  getNarrative,
  laudoDownloadBasename,
  LaudoError,
  triggerDocxDownload,
  triggerPdfDownload,
  type NarrativeRecordData,
} from '@/lib/laudo';
import { getCase, type CaseRecordData } from '@/lib/cases';
import { getWorkspace, type CaseAnalysisData } from '@/lib/case_workspace';
import { listSignatures, type SignatureRecordData } from '@/lib/case_signatures';
import {
  ContestadaTabs,
  deriveContestadaTabs,
  type ContestadaTab,
} from '@/components/LaudoContestadaTabs';
import { Blueprint } from '@/components/ui';
import { LaudoNarrativeEditor } from '@/components/LaudoNarrativeEditor';
import { LaudoPreview } from '@/components/LaudoPreview';
import { CadeiaCustodiaPanel } from '@/components/CadeiaCustodiaPanel';
import { InformacoesRelevantesPanel } from '@/components/InformacoesRelevantesPanel';
import { LaudoReviewPanel } from '@/components/LaudoReviewPanel';
import { useCurrentRole } from '@/lib/auth/useCurrentRole';
import { LaudoHeader } from '@/pages/LaudoHeader';
import { LoadErrorState, LoadingState, NotReadyState } from '@/pages/LaudoCasoPageStates';

const STATUS_TIMEOUT_MS = 15000;
// E36.F5/ADR-0050: cadência do polling enquanto a narrativa regenera (a regen
// leva poucos segundos; 2s deixa o texto novo + a aprovação resetada aparecerem
// sem refresh manual).
const REGEN_POLL_MS = 2000;

function reasonToMessage(reason: LaudoError['reason']): string {
  switch (reason) {
    case 'network':
      return ptBR.laudoCaso.errorNetwork;
    case 'render_failed':
      return ptBR.laudoCaso.errorRenderFailed;
    case 'not_ready':
      return ptBR.laudoCaso.notReadyBody;
    case 'narrative_not_ready':
      return ptBR.laudoCaso.errorNarrativeNotReady;
    case 'narrative_not_approved':
      return ptBR.laudoCaso.errorNarrativeNotApproved;
    case 'case_not_found':
      return ptBR.laudoCaso.errorGeneric;
    default:
      return ptBR.laudoCaso.errorGeneric;
  }
}

interface LaudoData {
  caseRecord: CaseRecordData | null;
  analyses: CaseAnalysisData[];
  signatures: SignatureRecordData[];
  narrativesByAnalysisId: Record<string, NarrativeRecordData | null>;
  initialActiveAnalysisId: string | null;
}

async function fetchInitialData(
  client: ReturnType<typeof useApiClient>,
  caseId: string,
): Promise<LaudoData> {
  const [record, workspaceAnalyses, sigs] = await Promise.all([
    getCase(client, caseId),
    getWorkspace(client, caseId),
    listSignatures(client, caseId),
  ]);
  const completed = workspaceAnalyses.filter((a) => a.status === 'completed');
  const entries = await Promise.all(
    completed.map(async (a) => {
      const n = await getNarrative(client, caseId, a.id).catch(() => null);
      return [a.id, n] as const;
    }),
  );
  const map: Record<string, NarrativeRecordData | null> = {};
  for (const [id, n] of entries) map[id] = n;
  return {
    caseRecord: record,
    analyses: workspaceAnalyses,
    signatures: sigs,
    narrativesByAnalysisId: map,
    initialActiveAnalysisId: completed[0]?.id ?? null,
  };
}

function useLaudoData(
  client: ReturnType<typeof useApiClient>,
  caseId: string,
): {
  data: LaudoData | null;
  loading: boolean;
  loadError: string | null;
} {
  const [data, setData] = useState<LaudoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (!caseId) return;
    let cancelled = false;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), STATUS_TIMEOUT_MS);

    void (async () => {
      try {
        const result = await fetchInitialData(client, caseId);
        if (!cancelled) setData(result);
      } catch (err) {
        if (cancelled) return;
        const message =
          err instanceof LaudoError ? reasonToMessage(err.reason) : ptBR.laudoCaso.errorGeneric;
        setLoadError(message);
      } finally {
        if (!cancelled) setLoading(false);
        clearTimeout(timeout);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeout);
    };
  }, [caseId, client]);

  return { data, loading, loadError };
}

function computeDownloadBlocked(
  tabs: ContestadaTab[],
  narrativesByAnalysisId: Record<string, NarrativeRecordData | null>,
): { allApproved: boolean; blockedMessage: string | null } {
  if (tabs.length === 0) {
    return { allApproved: false, blockedMessage: ptBR.laudoCaso.blockedDownload.missingNarrative };
  }
  const someMissing = tabs.some((t) => narrativesByAnalysisId[t.caseAnalysisId] == null);
  if (someMissing) {
    return { allApproved: false, blockedMessage: ptBR.laudoCaso.blockedDownload.missingNarrative };
  }
  const allApproved = tabs.every(
    (t) => narrativesByAnalysisId[t.caseAnalysisId]?.approved_at != null,
  );
  return {
    allApproved,
    blockedMessage: allApproved ? null : ptBR.laudoCaso.blockedDownload.notApprovedSome,
  };
}

/**
 * Resolve a prontidão do download a partir dos dois gates (narrativa + revisão).
 * E34.F1/ADR-0046: o gate de revisão só bloqueia quando `requireReviewerApproval`
 * (flag de config) está on. Extraído do componente para manter a complexidade
 * ciclomática dentro do limite do lint.
 */
function computeDownloadState(params: {
  allApproved: boolean;
  narrativeBlockedMessage: string | null;
  requireReviewerApproval: boolean;
  reviewApproved: boolean;
  anyRegenerating: boolean;
}): { downloadReady: boolean; downloadBlockedMessage: string | null } {
  const { allApproved, narrativeBlockedMessage, requireReviewerApproval, reviewApproved } = params;
  // E36.F5/ADR-0050: enquanto a narrativa regenera, o download fica bloqueado
  // (o texto vai mudar e a aprovação foi resetada). Precede os demais gates.
  if (params.anyRegenerating) {
    return {
      downloadReady: false,
      downloadBlockedMessage: ptBR.laudoCaso.blockedDownload.regenerating,
    };
  }
  if (!allApproved) {
    return { downloadReady: false, downloadBlockedMessage: narrativeBlockedMessage };
  }
  if (requireReviewerApproval && !reviewApproved) {
    return {
      downloadReady: false,
      downloadBlockedMessage: ptBR.laudoCaso.blockedDownload.reviewNotApproved,
    };
  }
  return { downloadReady: true, downloadBlockedMessage: null };
}

export function LaudoGrafotecnicaPage(): JSX.Element {
  const { id: caseId = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const client = useApiClient();
  const { data, loading, loadError } = useLaudoData(client, caseId);

  const [narrativesByAnalysisId, setNarrativesByAnalysisId] = useState<
    Record<string, NarrativeRecordData | null>
  >({});
  const [analyses, setAnalyses] = useState<CaseAnalysisData[]>([]);
  const [activeAnalysisId, setActiveAnalysisId] = useState<string | null>(null);
  const [caseRecord, setCaseRecord] = useState<CaseRecordData | null>(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportingDocx, setExportingDocx] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);
  // E85.F2: o código de verificação do último arquivo baixado nesta tela.
  const [codigoDoArquivo, setCodigoDoArquivo] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setNarrativesByAnalysisId(data.narrativesByAnalysisId);
    setActiveAnalysisId(data.initialActiveAnalysisId);
    setCaseRecord(data.caseRecord);
    setAnalyses(data.analyses);
  }, [data]);

  // E36.F5/ADR-0050: após um ajuste de LR na mesa, a narrativa é regenerada em
  // background. `narrative_regenerating` (vindo do workspace) liga o indicador e
  // bloqueia aprovar/baixar. Enquanto alguma análise regenera, faz polling do
  // workspace + narrativas para o texto novo (e a aprovação resetada) aparecerem
  // sem refresh manual; o efeito se desliga quando nenhuma está regenerando.
  const regeneratingByAnalysisId = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const a of analyses) map[a.id] = a.narrative_regenerating === true;
    return map;
  }, [analyses]);
  const anyRegenerating = useMemo(
    () => analyses.some((a) => a.narrative_regenerating === true),
    [analyses],
  );

  useEffect(() => {
    if (!caseId || !anyRegenerating) return undefined;
    let cancelled = false;
    const interval = setInterval(() => {
      void (async () => {
        try {
          const fresh = await getWorkspace(client, caseId);
          if (cancelled) return;
          setAnalyses(fresh);
          const completed = fresh.filter((a) => a.status === 'completed');
          const entries = await Promise.all(
            completed.map(
              async (a) =>
                [a.id, await getNarrative(client, caseId, a.id).catch(() => null)] as const,
            ),
          );
          if (cancelled) return;
          setNarrativesByAnalysisId((prev) => {
            const next = { ...prev };
            for (const [id, n] of entries) next[id] = n;
            return next;
          });
        } catch {
          // Silencioso — tenta de novo no próximo tick.
        }
      })();
    }, REGEN_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [caseId, client, anyRegenerating]);

  const tabs = useMemo<ContestadaTab[]>(
    () => deriveContestadaTabs(data?.analyses ?? [], data?.signatures ?? []),
    [data],
  );

  const { allApproved, blockedMessage: narrativeBlockedMessage } = useMemo(
    () => computeDownloadBlocked(tabs, narrativesByAnalysisId),
    [tabs, narrativesByAnalysisId],
  );

  // E23.F7: segundo gate, ortogonal ao da narrativa — o caso precisa estar
  // homologado por um revisor. E34.F1/ADR-0046: esse gate é OPCIONAL, ligado
  // pela flag `require_reviewer_approval` (default off). Quando off, o download
  // exige apenas as narrativas aprovadas e o painel de homologação some.
  const { requireReviewerApproval } = useCurrentRole();
  const { downloadReady, downloadBlockedMessage } = computeDownloadState({
    allApproved,
    narrativeBlockedMessage,
    requireReviewerApproval,
    reviewApproved: caseRecord?.review_status === 'approved',
    anyRegenerating,
  });

  // E23.F8: bump para a pré-visualização re-buscar quando uma narrativa muda
  // fora dela (editor legado por campo, geração, aprovação/reabertura).
  const [previewReload, setPreviewReload] = useState(0);

  const handleNarrativeChanged = useCallback(
    (analysisId: string, n: NarrativeRecordData | null) => {
      setNarrativesByAnalysisId((prev) => ({ ...prev, [analysisId]: n }));
      setPreviewReload((count) => count + 1);
    },
    [],
  );

  const handleExportPdf = useCallback(async () => {
    if (!caseId || !caseRecord || !downloadReady) return;
    setExportingPdf(true);
    setExportError(null);
    try {
      const { blob, codigoVerificacao } = await getLaudoPdfBlob(client, caseId);
      triggerPdfDownload(blob, laudoDownloadBasename(caseRecord));
      setCodigoDoArquivo(codigoVerificacao);
    } catch (err) {
      const message =
        err instanceof LaudoError ? reasonToMessage(err.reason) : ptBR.laudoCaso.errorPdfGeneric;
      setExportError(message);
    } finally {
      setExportingPdf(false);
    }
  }, [downloadReady, caseId, caseRecord, client]);

  const handleExportDocx = useCallback(async () => {
    if (!caseId || !caseRecord || !downloadReady) return;
    setExportingDocx(true);
    setExportError(null);
    try {
      const { blob, codigoVerificacao } = await getLaudoDocxBlob(client, caseId);
      triggerDocxDownload(blob, laudoDownloadBasename(caseRecord));
      setCodigoDoArquivo(codigoVerificacao);
    } catch (err) {
      const message =
        err instanceof LaudoError ? reasonToMessage(err.reason) : ptBR.laudoCaso.errorDocxGeneric;
      setExportError(message);
    } finally {
      setExportingDocx(false);
    }
  }, [downloadReady, caseId, caseRecord, client]);

  const handleBack = useCallback(() => {
    navigate(`/casos/${caseId}/workspace`);
  }, [caseId, navigate]);

  if (loading) return <LoadingState />;
  if (loadError) return <LoadErrorState message={loadError} onBack={handleBack} />;
  if (!caseRecord || tabs.length === 0) return <NotReadyState onBack={handleBack} />;

  const activeTab = tabs.find((t) => t.caseAnalysisId === activeAnalysisId);

  const globalStatusLabel = deriveGlobalStatusLabel(narrativesByAnalysisId);
  const lastGenerated = deriveLastGenerated(narrativesByAnalysisId);

  return (
    <article className="laudo-page" data-testid="laudo-page">
      {/* Capa — E18.F3.T02: topbar full-bleed via .laudo-page__header (sem card wrapper). */}
      <LaudoHeader
        caseRecord={caseRecord}
        canDownload={downloadReady}
        exportingPdf={exportingPdf}
        exportingDocx={exportingDocx}
        downloadBlockedMessage={downloadBlockedMessage}
        exportError={exportError}
        codigoDoArquivo={codigoDoArquivo}
        globalStatusLabel={globalStatusLabel}
        lastGenerated={lastGenerated}
        onBack={handleBack}
        onExportPdf={handleExportPdf}
        onExportDocx={handleExportDocx}
      />
      {/* E23.F23: coluna única, sem sidebar — o documento (PDF) é o protagonista.
          Barra compacta de ciclo (tabs + gerar/aprovar) no topo, depois o PDF
          grande e as ferramentas de edição em largura cheia (dentro de LaudoPreview). */}
      <div className="laudo-page__layout flex flex-col gap-sc-4">
        {/* E65.F1.T01: a ação que produz o laudo é a PRIMEIRA do bloco principal.
            Até aqui a homologação — que só existe com a flag da ADR-0046 ligada e
            que só faz sentido DEPOIS do texto pronto — ficava acima dela,
            invertendo a ordem do trabalho real (item 9 do E2E do owner). */}
        <Blueprint as="section" data-laudo-section="analise" data-testid="laudo-section-analise">
          <ContestadaTabs
            tabs={tabs}
            activeAnalysisId={activeAnalysisId}
            narrativesByAnalysisId={narrativesByAnalysisId}
            onSelect={setActiveAnalysisId}
          />
          {activeTab && (
            <div className="p-sc-4">
              <LaudoNarrativeEditor
                key={activeTab.caseAnalysisId}
                caseId={caseId}
                caseAnalysisId={activeTab.caseAnalysisId}
                contestadaLetra={activeTab.letra}
                narrative={narrativesByAnalysisId[activeTab.caseAnalysisId] ?? null}
                onNarrativeChanged={handleNarrativeChanged}
                regenerating={regeneratingByAnalysisId[activeTab.caseAnalysisId] ?? false}
              />
            </div>
          )}
        </Blueprint>
        {/* E34.F1/ADR-0046: painel de homologação só aparece quando a flag exige
            revisor; com a flag off (default) o perito baixa sem revisão. */}
        {requireReviewerApproval && (
          // `previewReload` sobe a cada mudança de narrativa (gerar/aprovar/
          // reabrir) — e aprovar narrativa é uma das três fontes de autoria da
          // ADR-0068 §3. Sem esse bump o painel decidiria sobre o retrato tirado
          // na montagem da página (F-E63.F1-09).
          <LaudoReviewPanel
            caseRecord={caseRecord}
            onReviewed={setCaseRecord}
            revalidateKey={previewReload}
          />
        )}
        <LaudoPreview caseId={caseId} reloadToken={previewReload} />
        {/* E58.F2 (ADR-0067): a mesma leitura que sai no Apêndice B do
            documento — a tela não pode divergir do laudo assinado. */}
        {/* E58.F3: achado autoral do perito, fora da comparação formal. */}
        <InformacoesRelevantesPanel caseRecord={caseRecord} onSaved={setCaseRecord} />
        <CadeiaCustodiaPanel caseId={caseId} />
      </div>
    </article>
  );
}

const LAST_GENERATED_FMT = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

function deriveGlobalStatusLabel(narratives: Record<string, NarrativeRecordData | null>): string {
  const values = Object.values(narratives);
  if (values.length === 0 || values.every((n) => n === null)) {
    return ptBR.laudoCaso.headerStatusGlobal.rascunho;
  }
  if (values.every((n) => n !== null && n.approved_at !== null)) {
    return ptBR.laudoCaso.headerStatusGlobal.aprovada;
  }
  return ptBR.laudoCaso.headerStatusGlobal.editando;
}

function deriveLastGenerated(narratives: Record<string, NarrativeRecordData | null>): string {
  const updates = Object.values(narratives)
    .filter((n): n is NarrativeRecordData => n !== null)
    .map((n) => n.updated_at)
    .filter((s): s is string => typeof s === 'string' && s.length > 0)
    .map((s) => new Date(s))
    .filter((d) => !Number.isNaN(d.getTime()));
  if (updates.length === 0) return ptBR.laudoCaso.headerLastGeneratedNever;
  const max = updates.reduce((a, b) => (a.getTime() >= b.getTime() ? a : b));
  return ptBR.laudoCaso.headerLastGenerated.replace('{{date}}', LAST_GENERATED_FMT.format(max));
}
